import { ACCENT } from './accent';
import type { AccentKey } from './accent';
import { CONTROL_KIND } from './control';
import type { ControlKind } from './control';
import { LOAD_ICON } from './icon';
import type { LoadIconKey } from './icon';

/**
 * What a load *is*. The type fixes how a guest controls it and which hardware
 * output can drive it; everything else about a load is free-form.
 */
export const LOAD_TYPE = {
  RELAY: 'relay',
  PHASE_CUT: 'phase_cut',
  ONE_TEN_V: 'one_ten_v',
  FAN: 'fan',
  CURTAIN: 'curtain',
} as const;

export type LoadTypeKey = (typeof LOAD_TYPE)[keyof typeof LOAD_TYPE];

/** Non-empty by construction: every output can drive at least one load type. */
export type AcceptedLoadTypes = readonly [LoadTypeKey, ...LoadTypeKey[]];

export interface LoadTypeSpec {
  readonly label: string;
  readonly control: ControlKind;
  /** What the driving output can do, shown on channel tiles. */
  readonly outputHint: string;
  readonly accent: AccentKey;
  readonly defaultIcon: LoadIconKey;
}

export const LOAD_TYPE_SPECS = {
  [LOAD_TYPE.RELAY]: {
    label: 'Switched (non-dim)',
    control: CONTROL_KIND.TOGGLE,
    outputHint: 'On / Off',
    accent: ACCENT.SLATE,
    defaultIcon: LOAD_ICON.LIGHTBULB,
  },
  [LOAD_TYPE.PHASE_CUT]: {
    label: 'Mains dimmable',
    control: CONTROL_KIND.LEVEL,
    outputHint: 'Dim 0–100%',
    accent: ACCENT.AMBER,
    defaultIcon: LOAD_ICON.CEILING,
  },
  [LOAD_TYPE.ONE_TEN_V]: {
    label: '0–10V dimmable',
    control: CONTROL_KIND.LEVEL,
    outputHint: 'Dim 0–100%',
    accent: ACCENT.VIOLET,
    defaultIcon: LOAD_ICON.STRIP,
  },
  [LOAD_TYPE.FAN]: {
    label: 'Fan',
    // The fan module takes the same 0–100 scale as the dimmers, so the guest
    // control is a level, not a switch.
    control: CONTROL_KIND.LEVEL,
    outputHint: 'Speed 0–100%',
    accent: ACCENT.SKY,
    defaultIcon: LOAD_ICON.FAN,
  },
  [LOAD_TYPE.CURTAIN]: {
    label: 'Curtain / blind',
    control: CONTROL_KIND.POSITION,
    outputHint: 'Open / Close',
    accent: ACCENT.TEAL,
    defaultIcon: LOAD_ICON.BLIND,
  },
} as const satisfies Record<LoadTypeKey, LoadTypeSpec>;

/** Declaration order used by pickers and summaries. */
export const LOAD_TYPE_KEYS = [
  LOAD_TYPE.RELAY,
  LOAD_TYPE.PHASE_CUT,
  LOAD_TYPE.ONE_TEN_V,
  LOAD_TYPE.FAN,
  LOAD_TYPE.CURTAIN,
] as const satisfies readonly LoadTypeKey[];

/** Runtime guard for values arriving from storage. */
export const isLoadTypeKey = (value: string): value is LoadTypeKey =>
  LOAD_TYPE_KEYS.some((key) => key === value);
